import {useMemo} from "react";
import {
    noteReactionsUrl,
    recordCollectionUrl,
    recordItemUrl,
    recordRestoreUrl,
    recordUrl,
    type RecordCollection,
} from "../services/categoryProfileUrls";
import type {
    CategoryRecord,
    CategoryRisk,
    RiskInput,
    Note,
    NoteInput,
    Requirement,
    RequirementInput,
    TeamMember,
    TeamMemberInput,
} from "../types/categoryRecordTypes";
import {done, failed} from "./notices";
import {useRecordWrite} from "./useCategoryRecord";

function requirementForm(input: Partial<RequirementInput>, file?: File | null, removeAttachment = false): FormData {
    const form = new FormData();
    Object.entries(input).forEach(([k, v]) => v !== undefined && form.append(k, v));
    if (file) form.append('attachment', file);
    if (removeAttachment) form.append('removeAttachment', 'true');
    return form;
}

/**
 * Every write on the category record, with its notice and Undo. Each returns a promise that settles after the
 * notice, so dialogs can close or stay open; errors are shown here and rethrown.
 */
export function useRecordActions(databagId: number, path: string, record: CategoryRecord | undefined) {
    const write = useRecordWrite(databagId, path);

    return useMemo(() => {
        const item = (c: RecordCollection, id: number) => recordItemUrl(databagId, c, id);
        const run = async <T>(fn: () => Promise<T>): Promise<T> => {
            try {
                return await fn();
            } catch (e) {
                failed(e);
                throw e;
            }
        };
        const remove = (c: RecordCollection, id: number, what: string) => run(async () => {
            await write('delete', item(c, id));
            done(`${what} removed.`, () => write('post', recordRestoreUrl(databagId, c, id)));
        });

        return {
            setSpecification: (specification: string) => run(async () => {
                const previous = record?.specification ?? '';
                if (specification === previous) return;
                await write('patch', recordUrl(databagId, path), {specification});
                done('Specification saved.', () => write('patch', recordUrl(databagId, path), {specification: previous}));
            }),

            /** Set or clear (null) the category's annual budget. */
            setBudget: (budget: {amount: number; year: number} | null) => run(async () => {
                const previous = record?.budget ?? null;
                if (JSON.stringify(previous) === JSON.stringify(budget)) return;
                await write('patch', recordUrl(databagId, path), {budget});
                done(budget ? 'Budget saved.' : 'Budget cleared.',
                    () => write('patch', recordUrl(databagId, path), {budget: previous}));
            }),

            setOwner: (ownerId: number | null) => run(async () => {
                const previous = record?.ownerId ?? null;
                if (ownerId === previous) return;
                await write('patch', recordUrl(databagId, path), {ownerId});
                done(ownerId === null ? 'Owner cleared.' : 'Owner changed.',
                    () => write('patch', recordUrl(databagId, path), {ownerId: previous}));
            }),

            /** `name`: the user's name, for the notice. */
            addTeamMember: (input: TeamMemberInput, name: string, makeOwner = false) => run(async () => {
                const {id} = await write('post', recordCollectionUrl(databagId, 'team', path), {...input, makeOwner});
                done(`${name} added${makeOwner ? ' as category owner' : ''}.`,
                    () => write('delete', item('team', id!)));
            }),

            updateTeamMember: (member: TeamMember, change: Partial<Pick<TeamMember, 'role' | 'raci'>>) => run(async () => {
                const previous = Object.fromEntries(Object.keys(change).map(k => [k, member[k as 'role' | 'raci']]));
                await write('patch', item('team', member.id), change);
                done(`${member.name} updated.`, () => write('patch', item('team', member.id), previous));
            }),

            removeTeamMember: (member: TeamMember) => remove('team', member.id, member.name),

            addRequirement: (input: RequirementInput, file: File | null) => run(async () => {
                const {id} = await write('post', recordCollectionUrl(databagId, 'requirements', path),
                    requirementForm(input, file));
                done('Requirement added.', () => write('delete', item('requirements', id!)));
            }),

            /** Field edits can be undone; a replaced or removed attachment cannot be put back from here. */
            updateRequirement: (req: Requirement, input: RequirementInput, file: File | null, removeAttachment: boolean) =>
                run(async () => {
                    await write('patch', item('requirements', req.id), requirementForm(input, file, removeAttachment));
                    const previous = {label: req.label, statement: req.statement, criterion: req.criterion};
                    done('Requirement updated.', file || removeAttachment
                        ? undefined
                        : () => write('patch', item('requirements', req.id), requirementForm(previous)));
                }),

            removeRequirement: (req: Requirement) => remove('requirements', req.id, 'Requirement'),

            /** A risk the team adds, or (with `source`) their version of a detected flag; Undo removes it again. */
            addRisk: (input: RiskInput & {source?: string}, message = 'Risk added.') => run(async () => {
                const {id} = await write('post', recordCollectionUrl(databagId, 'risks', path), input);
                done(message, () => write('delete', item('risks', id!)));
            }),

            updateRisk: (risk: CategoryRisk, change: RiskInput, message = 'Risk updated.') => run(async () => {
                const previous = Object.fromEntries(Object.keys(change).map(k => [k, risk[k as keyof CategoryRisk]]));
                await write('patch', item('risks', risk.id), change);
                done(message, () => write('patch', item('risks', risk.id), previous));
            }),

            removeRisk: (risk: CategoryRisk) => remove('risks', risk.id, 'Risk'),

            postNote: (input: NoteInput, parentId: number | null = null) => run(async () => {
                const {id} = await write('post', recordCollectionUrl(databagId, 'notes', path), {...input, parentId});
                done(parentId ? 'Reply posted.' : 'Note posted.', () => write('delete', item('notes', id!)));
            }),

            /** Edit a note's body, and for a top-level note its title and internal flag. */
            editNote: (note: Note, change: Partial<Pick<NoteInput, 'body' | 'title' | 'internal'>>) => run(async () => {
                const changed = Object.fromEntries(Object.entries(change)
                    .filter(([k, v]) => v !== (note[k as keyof Note] ?? '')));
                if (Object.keys(changed).length === 0) return;
                const previous = Object.fromEntries(Object.keys(changed).map(k => [k, note[k as keyof Note] ?? '']));
                await write('patch', item('notes', note.id), changed);
                done(note.status ? 'Note updated.' : 'Reply updated.', () => write('patch', item('notes', note.id), previous));
            }),

            setNoteStatus: (post: Note, status: 'open' | 'resolved') => run(async () => {
                await write('patch', item('notes', post.id), {status});
                done(status === 'resolved' ? 'Marked as resolved.' : 'Reopened.',
                    () => write('patch', item('notes', post.id), {status: post.status}));
            }),

            removeNote: (post: Note) => remove('notes', post.id, post.status ? 'Note' : 'Reply'),

            toggleReaction: (post: Note, reaction: string) =>
                run(() => write('post', noteReactionsUrl(databagId, post.id), {reaction})),
        };
    }, [write, databagId, path, record]);
}

export type RecordActions = ReturnType<typeof useRecordActions>;
